import { useState } from "react";
import { Helmet } from "react-helmet";
import BackButton from "../components/BackButton";

const currencies = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "JPY", symbol: "¥" },
  { code: "AED", symbol: "AED" },
  { code: "CNY", symbol: "¥" },
  { code: "CAD", symbol: "C$" },
  { code: "AUD", symbol: "A$" },
  { code: "CHF", symbol: "CHF" },
  { code: "SGD", symbol: "S$" },
  { code: "KRW", symbol: "₩" },
  { code: "NZD", symbol: "NZ$" },
  { code: "MYR", symbol: "RM" },
  { code: "HKD", symbol: "HK$" },
  { code: "ZAR", symbol: "R" },
  { code: "RUB", symbol: "₽" },
  { code: "BRL", symbol: "R$" },
  { code: "SEK", symbol: "kr" },
  { code: "NOK", symbol: "kr" },
  { code: "THB", symbol: "฿" },
];

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [tenure, setTenure] = useState("");
  const [tenureType, setTenureType] = useState("Years");
  const [currency, setCurrency] = useState("INR");
  const [emiResult, setEmiResult] = useState(null);

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate);
    let months = parseFloat(tenure);

    if (tenureType === "Years") {
      months *= 12;
    }

    const monthlyRate = annualRate / 12 / 100;

    const emi =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);

    const totalPayment = emi * months;
    const totalInterest = totalPayment - principal;

    setEmiResult({
      emi: emi.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      months,
    });
  };

  const reset = () => {
    setLoanAmount("");
    setInterestRate("");
    setTenure("");
    setEmiResult(null);
  };

  const formatDuration = (months) => {
    const y = Math.floor(months / 12);
    const m = months % 12;
    let result = "";
    if (y > 0) result += `${y} Year${y > 1 ? "s" : ""}`;
    if (y > 0 && m > 0) result += ", ";
    if (m > 0) result += `${m} Month${m > 1 ? "s" : ""}`;
    return result || "0 Months";
  };

  const getSymbol = () =>
    currencies.find((c) => c.code === currency)?.symbol || "₹";

  const symbol = getSymbol();

  return (
    <>
        <Helmet>
            <title>Advanced EMI Calculator - Free Loan & Interest Breakdown</title>
            <meta
            name="description"
            content="Free EMI calculator to estimate monthly payments, total interest, and total payable amount. Perfect for loans, home finance, and personal budgeting."
            />
            <meta
            name="keywords"
            content="EMI calculator, loan EMI tool, monthly EMI, interest calculator, finance calculator, advanced emi breakdown"
            />
            <meta name="robots" content="index, follow" />
            <link rel="canonical" href="https://freelity.com/emi-calculator" />
        </Helmet>
        <section className="sec-free-tools">
            <div className="container">
            <BackButton />
            <h2 className="w-75 mb-5">Advanced EMI Calculator with Multi-Currency Support | Accurate, Global & SEO Friendly</h2>

            <div className="row g-3">
                <div className="col-md-3">
                    <label>Loan Amount</label>
                <input
                    type="number"
                    className="form-control"
                    placeholder={`Loan Amount (${symbol})`}
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                />
                </div>
                <div className="col-md-3">
                    <label>Annual Interest Rate (%)</label>
                <input
                    type="number"
                    className="form-control"
                    placeholder="Annual Interest Rate (%)"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                />
                </div>
                <div className="col-md-3">
                    <label>Loan Tenure</label>
                    <div className="d-flex">
                        <input
                            type="number"
                            className="form-control"
                            placeholder="Loan Tenure"
                            value={tenure}
                            onChange={(e) => setTenure(e.target.value)}
                        />
                        <select
                            className="form-select ms-2"
                            value={tenureType}
                            onChange={(e) => setTenureType(e.target.value)}
                        >
                            <option>Years</option>
                            <option>Months</option>
                        </select>
                    </div>
                </div>
                <div className="col-md-3">
                    <label>Select Currency</label>
                    <select
                        className="form-select"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                    >
                        {currencies.map((c) => (
                        <option key={c.code} value={c.code}>
                            {c.symbol} - {c.code}
                        </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mt-4 d-flex gap-3">
                <button onClick={calculateEMI} className="btn btn-dark">
                Calculate EMI
                </button>
                <button onClick={reset} className="btn btn-secondary">
                Reset
                </button>
            </div>

            {emiResult && (
                <div className="card shadow-sm border-0 mt-5">
                <div className="card-body">
                    <h4 className="mb-3">EMI Breakdown</h4>
                    <ul className="list-group">
                    <li className="list-group-item d-flex justify-content-between">
                        Loan Amount (Principal):
                        <span>
                        {symbol} {parseFloat(loanAmount).toFixed(2)}
                        </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                        Monthly EMI:
                        <span>
                        {symbol} {emiResult.emi}
                        </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                        Total Interest Payable:
                        <span>
                        {symbol} {emiResult.totalInterest}
                        </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                        Total Payment (Principal + Interest):
                        <span>
                        {symbol} {emiResult.totalPayment}
                        </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                        Interest as % of Loan Amount:
                        <span>
                        {(
                            (parseFloat(emiResult.totalInterest) /
                            parseFloat(loanAmount)) *
                            100
                        ).toFixed(2)}{" "}
                        %
                        </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                        Total Duration:
                        <span>
                        {emiResult.months} Months (
                        {formatDuration(emiResult.months)})
                        </span>
                    </li>
                    </ul>

                    <div className="alert alert-info mt-4">
                    You will repay{" "}
                    <strong>
                        {symbol} {emiResult.totalInterest}
                    </strong>{" "}
                    extra as interest on a loan of{" "}
                    <strong>
                        {symbol} {parseFloat(loanAmount).toFixed(2)}
                    </strong>
                    .
                    <br />
                    Total amount to be paid after {emiResult.months} months (
                    {formatDuration(emiResult.months)}) is{" "}
                    <strong>
                        {symbol} {emiResult.totalPayment}
                    </strong>
                    .
                    </div>
                </div>
                </div>
            )}
            </div>
        </section>
    </>
  );
};

export default EMICalculator;
